<template>
   <div id="pdf-contract-form-wrapper">
      <div id="pdf-contract-form-inner-container">
         <div id="close-pdf-contract-form" @click="handlerCloseForm">X</div>
         <div class="pdf-contract-form-overflow">
            <span class="form-title">{{ $t("pdfContract.title") }}</span>
            <p class="form-info">
               {{ $t("pdfContract.info") }}
            </p>

            <!-- Feedback Component -->
            <div v-if="feedback.show" class="feedback-component" :class="feedback.type">
               <div class="feedback-icon">
                  <span v-if="feedback.type === 'success'">✓</span>
                  <span v-else>✕</span>
               </div>
               <div class="feedback-content">
                  <p>{{ feedback.message }}</p>
               </div>
               <button class="feedback-close" @click="feedback.show = false">×</button>
            </div>

            <form @submit.prevent="generatePDF">
               <div class="form-section">
                  <div class="input-container">
                     <label for="text_1hywu">{{ $t("pdfContract.agencyName") }}:</label>
                     <input id="text_1hywu" v-model="formData.text_1hywu" type="text" :disabled="isLoading" />
                  </div>

                  <div class="input-container">
                     <label for="text_2irya">{{ $t("pdfContract.companyManager") }}:</label>
                     <input id="text_2irya" v-model="formData.text_2irya" type="text" :disabled="isLoading" />
                  </div>

                  <div class="input-container">
                     <label for="text_3kmhv">{{ $t("pdfContract.address") }}:</label>
                     <input id="text_3kmhv" v-model="formData.text_3kmhv" type="text" :disabled="isLoading" />
                  </div>

                  <div class="input-container">
                     <label for="text_4fnnt">{{ $t("pdfContract.phone") }}:</label>
                     <input id="text_4fnnt" v-model="formData.text_4fnnt" type="tel" :disabled="isLoading" />
                  </div>

                  <div class="input-container">
                     <label for="text_5gjim">{{ $t("pdfContract.email") }}:</label>
                     <input id="text_5gjim" v-model="formData.text_5gjim" type="email" :disabled="isLoading" />
                  </div>

                  <div class="input-container">
                     <label for="text_6rcid">{{ $t("pdfContract.legalForm") }}:</label>
                     <input id="text_6rcid" v-model="formData.text_6rcid" type="text" :disabled="isLoading" />
                  </div>

                  <div class="input-container">
                     <label for="text_7zsmp">{{ $t("pdfContract.taxNumber") }}:</label>
                     <input id="text_7zsmp" v-model="formData.text_7zsmp" type="text" :disabled="isLoading" />
                  </div>
               </div>

               <div class="consent-wrapper">
                  <span class="checkbox" @click="handlerConsentConfirmation" :class="[consentConfirmed ? 'confirmed' : '', isLoading ? 'disabled' : '']"></span>
                  <p>{{ $t("pdfContract.consentText") }}</p>
               </div>

               <div id="pdf-contract-form-actions">
                  <button type="submit" class="gts-primary" :disabled="!canDownloadPdf || isLoading || pdfDownloaded">
                     <span v-if="!isLoading">{{ $t("pdfContract.downloadPdf") }}</span>
                     <div class="loader" v-if="isLoading"></div>
                  </button>
               </div>
            </form>

            <div v-if="showUpload" class="upload-section">
               <!-- File count warning -->
               <div class="file-requirement-warning" :class="hasEnoughFiles ? 'success' : 'warning'">
                  <div class="warning-icon">
                     <span v-if="hasEnoughFiles">✓</span>
                     <span v-else>⚠</span>
                  </div>
                  <div class="warning-content">
                     <p v-if="hasEnoughFiles">{{ $t("pdfContract.fileWarningSuccess", { current: uploadedFiles.length, count: pageCount }) }}</p>
                     <div v-else>
                        <!-- <strong>{{ $t("pdfContract.fileWarningImportant") }}</strong> -->
                        <ul class="form-warning-informations-wrapper">
                           <li class="form-warning-informations-item" v-if="false">
                              <span class="form-warning-informations-item-title"> {{ $t("pdfContract.fileWarningText") }}</span>
                              <span class="form-warning-informations-item-description">
                                 {{ $t("pdfContract.fileWarningText", { count: pageCount, current: uploadedFiles.length }) }}
                              </span>
                           </li>
                           <li class="form-warning-informations-item">
                              <span class="form-warning-informations-item-title">
                                 {{ $t("pdfContract.fileWarningTitleStep01") }}
                              </span>
                              <span class="form-warning-informations-item-description">
                                 {{ $t("pdfContract.fileWarningDescriptionStep01") }}
                              </span>
                           </li>
                           <li class="form-warning-informations-item">
                              <span class="form-warning-informations-item-title">
                                 {{ $t("pdfContract.fileWarningTitleStep02") }}
                              </span>
                              <span class="form-warning-informations-item-description">
                                 {{ $t("pdfContract.fileWarningDescriptionStep02") }}
                              </span>
                           </li>
                           <li class="form-warning-informations-item">
                              <span class="form-warning-informations-item-title">
                                 {{ $t("pdfContract.fileWarningTitleStep03") }}
                              </span>
                              <span class="form-warning-informations-item-description">
                                 {{ $t("pdfContract.fileWarningDescriptionStep03") }}
                              </span>
                           </li>
                        </ul>
                     </div>
                     <!-- <p class="alternative-info">{{ $t("pdfContract.alternativeUpload") }}</p> -->
                  </div>
               </div>

               <div class="upload-area" @dragover.prevent @drop="handleFileDrop">
                  <input ref="fileInput" type="file" multiple accept="image/jpeg,image/jpg,image/png,application/pdf,.pdf" @change="handleFileSelect" style="display: none" />
                  <div class="upload-content" @click="$refs.fileInput.click()">
                     <p>{{ $t("pdfContract.uploadInstruction") }}</p>
                     <div v-if="uploadedFiles.length" class="uploaded-files">
                        <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
                           <span>{{ file.name }}</span>
                           <button @click="removeFile(index)">&times;</button>
                        </div>
                     </div>
                  </div>
               </div>
               <div id="pdf-contract-form-actions">
                  <button @click="submitFiles" :disabled="!uploadedFiles.length || !hasEnoughFiles || isLoading" class="gts-primary">
                     <span v-if="!isLoading">{{ $t("pdfContract.submitFiles") }}</span>
                     <div class="loader" v-if="isLoading"></div>
                  </button>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { PDFDocument } from "pdf-lib";
import { applicationPDFForm } from "@/service/rest.api";

export default {
   name: "PdfContractForm",
   props: {
      pageCount: {
         type: Number,
         default: 3,
      },
   },
   data() {
      return {
         formData: {
            text_1hywu: "", // Agenturname
            text_2irya: "", // Geschäftsführer/ Inhaber
            text_3kmhv: "", // Anschrift
            text_4fnnt: "", // Festnetz/ Mobil
            text_5gjim: "", // E-Mail
            text_6rcid: "", // Rechtsform
            text_7zsmp: "", // Steuernr./ Ustld Nr.
         },
         showUpload: false,
         uploadedFiles: [],
         consentConfirmed: false,
         isLoading: false,
         pdfDownloaded: false,
         feedback: {
            show: false,
            type: "success", // 'success' or 'error'
            message: "",
         },
      };
   },
   computed: {
      canDownloadPdf() {
         const namePattern = /^[a-zA-ZäöüÄÖÜßçğıİöşüÇĞŞ\s]+$/;
         const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
         const phonePattern = /^\+?[\d\s-()]+$/;

         return (
            this.formData.text_1hywu.trim() !== "" && // Agenturname
            this.formData.text_2irya.trim() !== "" && // Geschäftsführer/ Inhaber
            namePattern.test(this.formData.text_2irya.trim()) &&
            this.formData.text_3kmhv.trim() !== "" && // Anschrift
            this.formData.text_4fnnt.trim() !== "" && // Festnetz/ Mobil
            phonePattern.test(this.formData.text_4fnnt.trim()) &&
            this.formData.text_5gjim.trim() !== "" && // E-Mail
            emailPattern.test(this.formData.text_5gjim.trim()) &&
            this.formData.text_6rcid.trim() !== "" && // Rechtsform
            this.formData.text_7zsmp.trim() !== "" && // Steuernr./ Ustld Nr.
            this.consentConfirmed
         );
      },
      hasEnoughFiles() {
         return this.uploadedFiles.length >= 1;
      },
   },
   methods: {
      async generatePDF() {
         this.isLoading = true;
         this.feedback.show = false;

         try {
            // 1. API'ye form verilerini gönder
            const query = {
               "agency-name": this.formData.text_1hywu,
               "company-officer": this.formData.text_2irya,
               "company-address": this.formData.text_3kmhv,
               "company-phone": this.formData.text_4fnnt,
               "company-email": this.formData.text_5gjim,
               "legal-form": this.formData.text_6rcid,
               "tax-number": this.formData.text_7zsmp,
            };

            const formData = new FormData();
            for (const field in query) {
               formData.append(field, query[field]);
            }

            // API'ye gönder
            await applicationPDFForm(formData);

            // 2. Template PDF'i yükle ve doldur
            console.log("PDF yükleniyor...");
            const response = await fetch("/agenturvertrag.pdf");
            console.log("PDF response:", response.status, response.ok);

            if (!response.ok) {
               throw new Error(`PDF yüklenemedi: ${response.status}`);
            }

            const pdfBytes = await response.arrayBuffer();
            console.log("PDF bytes yüklendi:", pdfBytes.byteLength);
            const pdfDoc = await PDFDocument.load(pdfBytes);
            console.log("PDF document yüklendi");

            // Form'u al ve alanları doldur
            const form = pdfDoc.getForm();

            // PDF'deki gerçek field isimlerini görelim
            const fields = form.getFields();
            console.log(
               "PDF'deki field'lar:",
               fields.map((f) => ({ name: f.getName(), type: f.constructor.name }))
            );

            // Form field'larını doldur
            const fieldMapping = {
               text_1hywu: this.formData.text_1hywu, // Agentur Name
               text_2irya: this.formData.text_2irya, // Geschäftsführer/ Inhaber
               text_3kmhv: this.formData.text_3kmhv, // Anschrift
               text_4fnnt: this.formData.text_4fnnt, // Festnetz/ Mobil
               text_5gjim: this.formData.text_5gjim, // E-Mail
               text_6rcid: this.formData.text_6rcid, // Rechtsform
               text_7zsmp: this.formData.text_7zsmp, // Steuernr./ Ustld Nr.
            };

            // Her field için değer set et
            for (const [fieldName, value] of Object.entries(fieldMapping)) {
               try {
                  const field = form.getTextField(fieldName);
                  if (field && value) {
                     // Türkçe karakterleri İngilizce karşılıklarıyla değiştir
                     const cleanValue = this.sanitizeForPDF(value.toString());
                     field.setText(cleanValue);
                  }
               } catch (error) {
                  console.warn(`Field ${fieldName} bulunamadı:`, error);
               }
            }

            // PDF'i serialize et
            const filledPdfBytes = await pdfDoc.save();

            // Blob oluştur ve indir
            const blob = new Blob([filledPdfBytes], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "agenturvertrag.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.pdfDownloaded = true;
            this.showFeedback("success", this.$t("pdfContract.feedbackPdfDownload"), false);
            this.showUpload = true;
         } catch (error) {
            console.error("PDF işleme hatası:", error);
            this.showFeedback("error", this.$t("pdfContract.feedbackError"));
         } finally {
            this.isLoading = false;
         }
      },

      handleFileSelect(event) {
         const files = Array.from(event.target.files);
         this.addFiles(files);
      },

      handleFileDrop(event) {
         event.preventDefault();
         const files = Array.from(event.dataTransfer.files);
         this.addFiles(files);
      },

      addFiles(files) {
         const validFiles = files.filter((file) => file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png" || file.type === "application/pdf");
         this.uploadedFiles.push(...validFiles);
      },

      removeFile(index) {
         this.uploadedFiles.splice(index, 1);
      },

      async submitFiles() {
         if (!this.uploadedFiles.length) return;

         if (!this.hasEnoughFiles) {
            this.showFeedback("error", this.$t("pdfContract.feedbackFileCountError", { count: this.pageCount, current: this.uploadedFiles.length }));
            return;
         }

         const query = {
            "agency-name": this.formData.text_1hywu,
            "company-officer": this.formData.text_2irya,
            "company-address": this.formData.text_3kmhv,
            "company-phone": this.formData.text_4fnnt,
            "company-email": this.formData.text_5gjim,
            "legal-form": this.formData.text_6rcid,
            "tax-number": this.formData.text_7zsmp,
         };

         const formData = new FormData();

         // Add form fields
         for (const field in query) {
            formData.append(field, query[field]);
         }

         // Add uploaded files to 'contracting' field
         this.uploadedFiles.forEach((file) => {
            formData.append("contracting[]", file); // [] kullan multiple files için
         });

         this.isLoading = true;
         this.feedback.show = false; // Hide any previous feedback
         try {
            const res = await applicationPDFForm(formData);
            console.log("Submit files response:", res);
            console.log("Response type:", typeof res);
            console.log("Has status property:", "status" in res);

            if (res && res.status == "mail_sent") {
               this.showFeedback("success", this.$t("pdfContract.feedbackFilesSuccess"));
               this.uploadedFiles = [];
               this.showUpload = false;
               this.resetForm();
            } else if (res instanceof Blob) {
               // Blob response geldi, muhtemelen başarılı
               this.showFeedback("success", this.$t("pdfContract.feedbackFilesSuccess"));
               this.uploadedFiles = [];
               this.showUpload = false;
               this.resetForm();
            } else {
               console.log("Unexpected response format:", res);
               this.showFeedback("error", this.$t("pdfContract.feedbackError"));
            }
         } catch (error) {
            console.error("Upload error:", error);
            this.showFeedback("error", this.$t("pdfContract.feedbackError"));
         } finally {
            this.isLoading = false;
         }
      },

      resetForm() {
         Object.keys(this.formData).forEach((key) => {
            this.formData[key] = "";
         });
         this.consentConfirmed = false;
      },

      handlerCloseForm() {
         this.$store.state.applicationForm = false;
      },

      handlerConsentConfirmation() {
         if (!this.isLoading) {
            this.consentConfirmed = !this.consentConfirmed;
         }
      },

      showFeedback(type, message, autoHide = true) {
         this.feedback = {
            show: true,
            type: type,
            message: message,
         };

         // Auto hide after 5 seconds (only if autoHide is true)
         if (autoHide) {
            setTimeout(() => {
               this.feedback.show = false;
            }, 5000);
         }
      },

      sanitizeForPDF(text) {
         // Türkçe ve özel karakterleri PDF-lib'in desteklediği karakterlerle değiştir
         const charMap = {
            ı: "i",
            İ: "I",
            ğ: "g",
            Ğ: "G",
            ü: "u",
            Ü: "U",
            ş: "s",
            Ş: "S",
            ö: "o",
            Ö: "O",
            ç: "c",
            Ç: "C",
            // Diğer özel karakterler
            â: "a",
            Â: "A",
            î: "i",
            Î: "I",
            û: "u",
            Û: "U",
            ê: "e",
            Ê: "E",
         };

         return text.replace(/[ıİğĞüÜşŞöÖçÇâÂîÎûÛêÊ]/g, (match) => charMap[match] || match);
      },
   },
};
</script>

<style scoped lang="scss">
.file-requirement-warning {
   align-items: flex-start;
}
.form-warning-informations-wrapper {
   li {
      display: flex;
      flex-direction: column;
      margin-bottom: 0.5rem;

      span.form-warning-informations-item-title {
         font-weight: 500;
      }
   }
}
#pdf-contract-form-wrapper {
   position: fixed;
   z-index: 99999;
   background: #00000050;
   top: 0;
   left: 0;
   bottom: 0;
   right: 0;

   #close-pdf-contract-form {
      position: absolute;
      right: 1rem;
      top: 1rem;
      background: #eaeaea;
      width: 32px;
      height: 32px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #4a4a4a;
      font-weight: 500;
      border-radius: 50%;
      cursor: pointer;
      border: 1px solid #b2b2b2;
   }

   #pdf-contract-form-inner-container {
      width: 92vw;
      height: fit-content;
      max-height: 90vh;
      position: relative;
      overflow: hidden;
      overflow-y: auto;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      margin: auto;
      position: absolute;
      background-color: #fff;
      border: 1px solid #ccc;
      box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
      border-radius: 0.5rem;
      padding: 2rem 1.5rem;

      .pdf-contract-form-overflow {
         width: 100%;

         .form-title {
            font-size: 1.25rem;
            font-weight: 500;
            color: #363636;
            margin-bottom: 1rem;
         }

         .form-info {
            font-size: 0.9rem;
            color: #403d3d;
            line-height: 1.3rem;
            padding: 0.5rem 0;
         }

         .feedback-component {
            display: flex;
            align-items: center;
            padding: 1rem;
            margin: 1rem 0;
            border-radius: 0.5rem;
            position: relative;
            animation: slideDown 0.3s ease-out;

            &.success {
               background-color: #d4edda;
               border: 1px solid #c3e6cb;
               color: #155724;

               .feedback-icon {
                  background-color: #28a745;
                  color: white;
               }
            }

            &.error {
               background-color: #f8d7da;
               border: 1px solid #f5c6cb;
               color: #721c24;

               .feedback-icon {
                  background-color: #dc3545;
                  color: white;
               }
            }

            .feedback-icon {
               width: 32px;
               height: 32px;
               border-radius: 50%;
               display: flex;
               align-items: center;
               justify-content: center;
               font-weight: bold;
               font-size: 1.2rem;
               margin-right: 1rem;
               flex-shrink: 0;
            }

            .feedback-content {
               flex: 1;

               p {
                  margin: 0;
                  font-weight: 500;
               }
            }

            .feedback-close {
               background: none;
               border: none;
               font-size: 1.5rem;
               cursor: pointer;
               padding: 0.25rem;
               line-height: 1;
               opacity: 0.7;
               margin-left: 1rem;

               &:hover {
                  opacity: 1;
               }
            }
         }

         @keyframes slideDown {
            from {
               opacity: 0;
               transform: translateY(-20px);
            }
            to {
               opacity: 1;
               transform: translateY(0);
            }
         }

         .form-section {
            display: grid;
            width: 100%;
            grid-template-columns: auto;
            margin: 1rem 0;

            .input-container {
               display: grid;
               grid-template-columns: 200px 1fr;
               gap: 0.75rem;
               align-items: center;
               margin-bottom: 0.5rem;

               @media (max-width: 576px) {
                  grid-template-columns: 1fr;
                  gap: 0.5rem;
               }

               label {
                  font-size: 0.85rem;
                  font-weight: 500;

                  @media (max-width: 576px) {
                     font-size: 0.8rem;
                     margin-bottom: 0.2rem;
                  }
               }

               input {
                  background-color: #eee;
                  border: 0;
                  outline: 0;
                  height: 38px;
                  font-size: 1rem;
                  text-indent: 0.5rem;
                  border-radius: 0.4rem;
                  border: 1px solid transparent;

                  &:focus {
                     border: 1px solid #33b8fb;
                  }

                  &:disabled {
                     background-color: #f5f5f5;
                     color: #999;
                     cursor: not-allowed;
                     opacity: 0.6;
                  }
               }
            }
         }

         .consent-wrapper {
            display: flex;
            margin: 1rem 0;
            border-radius: 0.4rem;
            padding: 0.5rem 1rem;
            align-items: center;
            justify-content: center;

            .checkbox {
               width: 18px;
               height: 18px;
               border: 0.125rem solid #32b8fb;
               flex-shrink: 0;
               border-radius: 0.2rem;
               cursor: pointer;

               &.confirmed {
                  background-color: #32b8fb;
                  cursor: pointer;

                  &:after {
                     content: "";
                     background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMC4wODQiIGhlaWdodD0iNy43NyIgdmlld0JveD0iMCAwIDEwLjA4NCA3Ljc3Ij48cGF0aCBkPSJNMTcuNTA4LDI0LjQyNmEuNjgyLjY4MiwwLDAsMS0uMzg3LS4xMmwtMy41NDYtMi40MzNhLjY4NS42ODUsMCwwLDEsLjc3NS0xLjEzbDMuMDIzLDIuMDc1LDQuNzctNS45MDdhLjY4NS42ODUsMCwxLDEsMS4wNjYuODYxbC01LjE2OCw2LjRBLjY4NC42ODQsMCwwLDEsMTcuNTA4LDI0LjQyNloiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0xMy4yNzggLTE2LjY1NykiIGZpbGw9IiNmZmYiLz48L3N2Zz4=");
                     background-size: 70%;
                     width: 100%;
                     height: 100%;
                     display: block;
                     background-repeat: no-repeat;
                     background-position: center;
                  }
               }

               &.disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                  border-color: #ccc;

                  &.confirmed {
                     background-color: #ccc;
                  }
               }
            }

            p {
               margin: 0;
               margin-left: 0.75rem;
               padding-left: 0.75rem;
               font-size: 0.9rem;
               line-height: 1.3rem;
               color: #484848;
               border-left: 1px solid #ccc;
            }
         }

         #pdf-contract-form-actions {
            width: 100%;
            display: flex;
            justify-content: center;
            margin: 1.5rem 0;

            button {
               margin: 0 0.5rem;
               max-height: 40px;
               width: fit-content;

               .loader {
                  width: 24px;
                  height: 24px;
                  border: 3px solid #fff;
                  border-bottom-color: transparent;
                  border-radius: 50%;
                  display: inline-block;
                  box-sizing: border-box;
                  animation: rotation 1s linear infinite;
               }

               @keyframes rotation {
                  0% {
                     transform: rotate(0deg);
                  }
                  100% {
                     transform: rotate(360deg);
                  }
               }
            }
         }

         .upload-section {
            margin-top: 1.5rem;
            padding-top: 0.75rem;
            border-top: 1px solid #ddd;

            h3 {
               margin-bottom: 0.75rem;
               color: #363636;
               font-size: 1rem;
               font-weight: 500;
            }

            .file-requirement-warning {
               display: flex;
               align-items: center;
               padding: 0.75rem;
               margin: 0.75rem 0;
               border-radius: 0.4rem;
               border: 1px solid;

               &.warning {
                  background-color: #fff3cd;
                  border-color: #ffeaa7;
                  color: #856404;

                  .warning-icon {
                     background-color: #ffc107;
                     color: white;
                  }
               }

               &.success {
                  background-color: #d1e7dd;
                  border-color: #badbcc;
                  color: #0f5132;

                  .warning-icon {
                     background-color: #28a745;
                     color: white;
                  }
               }

               .warning-icon {
                  width: 28px;
                  height: 28px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-weight: bold;
                  font-size: 1rem;
                  margin-right: 0.75rem;
                  flex-shrink: 0;
               }

               .warning-content {
                  flex: 1;

                  p {
                     margin: 0;
                     font-weight: 500;
                     font-size: 0.9rem;
                     line-height: 1.3;

                     &.alternative-info {
                        margin-top: 0.5rem;
                        font-weight: 400;
                        font-size: 0.85rem;
                        color: #666;
                        font-style: italic;
                     }
                  }
               }
            }
         }

         .upload-area {
            border: 2px dashed #ddd;
            border-radius: 6px;
            padding: 1.5rem;
            text-align: center;
            margin-bottom: 0.75rem;
            cursor: pointer;

            &:hover {
               border-color: #33b8fb;
            }

            p {
               margin: 0;
               color: #666;
               font-size: 0.9rem;
            }
         }

         .uploaded-files {
            margin-top: 0.75rem;
         }

         .file-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.4rem 0.5rem;
            background: #f5f5f5;
            border-radius: 4px;
            margin-bottom: 0.4rem;
            font-size: 0.9rem;

            button {
               background: #ff4444;
               color: white;
               border: none;
               border-radius: 50%;
               width: 20px;
               height: 20px;
               cursor: pointer;
               font-size: 14px;
               line-height: 1;
               display: flex;
               align-items: center;
               justify-content: center;
               padding: 0;
               flex-shrink: 0;
            }
         }
      }
   }
}

@media (min-width: 1200px) {
   #pdf-contract-form-wrapper #pdf-contract-form-inner-container {
      width: 70vw;
   }
}

@media (min-width: 1600px) {
   #pdf-contract-form-wrapper #pdf-contract-form-inner-container {
      width: 40vw;
   }
}
</style>
